// Shared backtracking engine behind T-spin puzzle generation. Ported from
// himitsuconfidential/downstack-practice — this exact machinery (try_drop,
// is_exposed, is_floatable, is_spinable, get_unstability,
// is_few_non_cheese_hole, try_a_piece, try_all_pieces, try_a_move,
// generate_a_ds_map, get_shuffled_holdable_queue) is duplicated near-verbatim
// across the reference repo's script4.js/script6.js; each T-spin variant only
// differs in its generate_final_map (the target shape) and win condition,
// which live in tspin.ts.

import { BOARD_HEIGHT, BOARD_WIDTH, PIECE_TYPES, type PieceType } from '../engine/constants';
import {
  cloneBoard,
  getMaxHeight,
  lockPiece,
  moveLeft,
  moveRight,
  rotate180,
  rotateCCW,
  rotateCW,
  shuffle,
  toShape,
  type Board,
  type Orientation,
  type PieceState,
} from '../engine/game';
import { REVERSE_HOLD_TABLE } from './reverseHoldTables';

export type TSpinMode = 'tsd' | 'stsd' | 'tst';

export interface GenOptions {
  pieceCount: number; // 2-7
  moreSkims: boolean; // rougher/skimmier garbage-line shapes
  moreHoles: boolean; // tolerate up to 1 non-cheese hole instead of 0
  uniquePieces: boolean; // cap each piece type to 1 use instead of 2
  flatterBoard: boolean; // reject target shapes with a wide height spread
}

export const DEFAULT_GEN_OPTIONS: GenOptions = {
  pieceCount: 7,
  moreSkims: false,
  moreHoles: false,
  uniquePieces: true,
  flatterBoard: true,
};

// Ported from header.js possible_piece_config_table: every (orientation,
// spawn-column) pair worth probing for a given piece type.
export const POSSIBLE_PIECE_CONFIG_TABLE: Record<PieceType, Array<[Orientation, number]>> = {
  O: pairs([0, 1, 2, 3], range(0, 8)),
  I: [...pairOne(0, range(1, 7)), ...pairOne(1, range(0, 9)), ...pairOne(2, range(1, 7)), ...pairOne(3, range(1, 8))],
  Z: [...pairOne(0, range(1, 8)), ...pairOne(1, range(1, 9)), ...pairOne(2, range(1, 8)), ...pairOne(3, range(0, 8))],
  S: [...pairOne(0, range(1, 8)), ...pairOne(1, range(1, 9)), ...pairOne(2, range(1, 8)), ...pairOne(3, range(0, 8))],
  T: [...pairOne(0, range(1, 8)), ...pairOne(1, range(1, 9)), ...pairOne(2, range(1, 8)), ...pairOne(3, range(0, 8))],
  J: [...pairOne(0, range(1, 8)), ...pairOne(1, range(1, 9)), ...pairOne(2, range(1, 8)), ...pairOne(3, range(0, 8))],
  L: [...pairOne(0, range(1, 8)), ...pairOne(1, range(1, 9)), ...pairOne(2, range(1, 8)), ...pairOne(3, range(0, 8))],
};

function range(lo: number, hi: number): number[] {
  const out: number[] = [];
  for (let i = lo; i <= hi; i++) out.push(i);
  return out;
}
function pairOne(ori: number, cols: number[]): Array<[Orientation, number]> {
  return cols.map((c) => [ori as Orientation, c]);
}
function pairs(oris: number[], cols: number[]): Array<[Orientation, number]> {
  const out: Array<[Orientation, number]> = [];
  for (const o of oris) for (const c of cols) out.push([o as Orientation, c]);
  return out;
}

export interface GenState {
  board: Board;
  piece: PieceState;
  addedLine: number[];
  boardHistory: Board[];
  pieceAdded: PieceType[];
  finishedMap: Board;
  moreSkims: boolean;
  lastInfo: string;
  /** Mutable override of opts.moreHoles — the reference engine flips this on
   * after 50 failed attempts for the taller/multi-notch T-spin shapes
   * (script6.js's advance-tspin-practice modes), since a strict 0-hole
   * tolerance is otherwise close to unsatisfiable for them. */
  moreHoles: boolean;
}

/** Safe line insert: shifts rows above `rowIdx` up and fills `rowIdx` with garbage. */
export function addLine(gen: GenState, rowIdx: number): void {
  if (rowIdx < 0 || rowIdx >= BOARD_HEIGHT) return; // guards an edge case the original leaves unbounded
  const { board } = gen;
  for (let col = 0; col < BOARD_WIDTH; col++) {
    for (let row = BOARD_HEIGHT - 1; row > rowIdx; row--) board[row][col] = board[row - 1][col];
    board[rowIdx][col] = 'G';
  }
  gen.addedLine.push(rowIdx);
}

export function addRandomLine(gen: GenState): void {
  const maxHeight = getMaxHeight(gen.board);
  const rowIndex = Math.floor(Math.random() * (maxHeight + 2));
  gen.addedLine = [];
  const probability = [0.1, 0.15, 0.3, 1];
  const rng = Math.random();
  if (rng < probability[0]) {
    addLine(gen, rowIndex);
    addLine(gen, rowIndex + 1);
    addLine(gen, rowIndex + 2);
  } else if (rng < probability[1] && rowIndex < maxHeight + 1) {
    addLine(gen, rowIndex);
    addLine(gen, rowIndex + 2);
  } else if (rng < probability[2]) {
    addLine(gen, rowIndex);
    addLine(gen, rowIndex + 1);
  } else if (rng < probability[3]) {
    addLine(gen, rowIndex);
  }
}

export function addRandomLineLessSkim(gen: GenState): void {
  const nonGarbage = new Array(BOARD_HEIGHT).fill(0);
  for (let row = 0; row < BOARD_HEIGHT; row++)
    for (let col = 0; col < BOARD_WIDTH; col++) if (gen.board[row][col] !== 'G') nonGarbage[row]++;
  let garbageHeight = 0;
  for (const n of nonGarbage) {
    if (n === 1) garbageHeight++;
    else break;
  }
  const rowIndex = garbageHeight;
  gen.addedLine = [];
  const probability = [0.1, 0.3, 1];
  const rng = Math.random();
  if (rng < probability[0]) {
    addLine(gen, rowIndex);
    addLine(gen, rowIndex + 1);
    addLine(gen, rowIndex + 2);
  } else if (rng < probability[1]) {
    addLine(gen, rowIndex);
    addLine(gen, rowIndex + 1);
  } else if (rng < probability[2]) {
    addLine(gen, rowIndex);
  }
}

/** Finds the shallowest piece-shaped pocket of solid garbage reachable from spawn; carves it to empty and moves the piece there. */
export function tryDrop(gen: GenState): boolean {
  const shape = toShape(gen.piece);
  const lowestHeight = Math.min(...shape.map(([, row]) => row));
  let minRelativeHeight = BOARD_HEIGHT;
  for (const [col, row] of shape) {
    let groundHeight = 0;
    for (let i = 0; i < row; i++) if (gen.board[i][col] === 'G') groundHeight = i + 1;
    minRelativeHeight = Math.min(minRelativeHeight, row - groundHeight);
  }
  for (let fall = minRelativeHeight + 1; fall <= lowestHeight; fall++) {
    let allGarbage = true;
    for (const [col, row] of shape) {
      if (gen.board[row - fall][col] !== 'G') {
        allGarbage = false;
        break;
      }
    }
    if (allGarbage) {
      for (const [col, row] of shape) gen.board[row - fall][col] = 'N';
      gen.piece.y -= fall;
      return true;
    }
  }
  return false;
}

export function isExposed(gen: GenState): boolean {
  for (const [col, row] of toShape(gen.piece)) {
    for (let j = row + 1; j < BOARD_HEIGHT; j++) if (gen.board[j][col] === 'G') return false;
  }
  return true;
}

export function isFloatable(gen: GenState): boolean {
  for (const [col, row] of toShape(gen.piece)) {
    if (row === 0 || gen.board[row - 1][col] === 'G') return false;
  }
  return true;
}

export function isSpinable(gen: GenState, depth = 1): boolean {
  if (depth > 2) return false;
  const pieceInfo: [number, number, Orientation] = [gen.piece.x, gen.piece.y, gen.piece.orientation];

  if (depth === 2) {
    if (moveLeft(gen.board, gen.piece, true)) {
      if (isExposed(gen) && !isFloatable(gen)) {
        gen.piece.x += 1;
        return true;
      }
    }
    if (moveRight(gen.board, gen.piece, true)) {
      if (isExposed(gen) && !isFloatable(gen)) {
        gen.piece.x -= 1;
        return true;
      }
    }
  }

  if (rotateCW(gen.board, gen.piece, true) !== null) {
    if ((isExposed(gen) && !isFloatable(gen)) || isSpinable(gen, depth + 1)) {
      rotateCCW(gen.board, gen.piece, true);
      if (pieceInfo[0] === gen.piece.x && pieceInfo[1] === gen.piece.y && pieceInfo[2] === gen.piece.orientation) {
        return true;
      }
    }
  }

  [gen.piece.x, gen.piece.y, gen.piece.orientation] = pieceInfo;
  if (rotateCCW(gen.board, gen.piece, true) !== null) {
    if ((isExposed(gen) && !isFloatable(gen)) || isSpinable(gen, depth + 1)) {
      rotateCW(gen.board, gen.piece, true);
      if (pieceInfo[0] === gen.piece.x && pieceInfo[1] === gen.piece.y && pieceInfo[2] === gen.piece.orientation) {
        return true;
      }
    }
  }

  [gen.piece.x, gen.piece.y, gen.piece.orientation] = pieceInfo;
  if (rotate180(gen.board, gen.piece, true) !== null) {
    if (isExposed(gen) && !isFloatable(gen)) {
      rotate180(gen.board, gen.piece, true);
      return true;
    }
  }

  return false;
}

export function getUnstability(gen: GenState): number {
  const grounded = new Set<string>();
  const ungrounded: Array<[number, number]> = [];
  for (let col = 0; col < BOARD_WIDTH; col++) {
    let isGrounded = true;
    for (let row = 0; row < BOARD_HEIGHT; row++) {
      if (gen.board[row][col] === 'G') {
        if (isGrounded) grounded.add(`${row},${col}`);
        else ungrounded.push([row, col]);
      } else {
        isGrounded = false;
      }
    }
  }
  let unstability = 0;
  for (const [row, col] of ungrounded) {
    const rightGrounded = col < BOARD_WIDTH - 1 && grounded.has(`${row},${col + 1}`);
    const leftGrounded = col > 0 && grounded.has(`${row},${col - 1}`);
    if (!(rightGrounded || leftGrounded)) unstability += 1;
  }
  return unstability;
}

export function isSmoothSeq(arr: number[]): boolean {
  let uped = false;
  let last: number | null = null;
  for (const ele of arr) {
    if (last === null) {
      last = ele;
      continue;
    }
    if (ele - last > 1) uped = true;
    else if (last - ele > 1 && uped) return false;
    last = ele;
  }
  return true;
}

export function isFewNonCheeseHole(gen: GenState, mode: TSpinMode, opts: GenOptions): boolean {
  const height: number[] = [];
  for (let col = 0; col < BOARD_WIDTH; col++) {
    let h = 0;
    for (let row = 0; row < BOARD_HEIGHT; row++) if (gen.board[row][col] === 'G') h = row;
    height.push(h);
  }
  if (!isSmoothSeq(height)) return false;
  if (opts.flatterBoard) {
    const sorted = [...height].sort((a, b) => a - b);
    if (sorted[8] - sorted[1] > 5) return false;
  }
  // The "cheese" metric below assumes a single-gap-per-row board (the shape
  // 'tsd's target is). stsd/tst targets are intentionally multi-column notch
  // structures a few rows tall — by that same metric they're many "holes"
  // deep even in their pristine, piece-less state (empirically, ~6 on a
  // fresh target vs. the threshold of 0-1), so the check can never pass and
  // generation starves at 0% before even trying placements. It's testing a
  // property these shapes don't have by design, so skip it for them.
  if (mode === 'stsd' || mode === 'tst') return true;
  const holes = new Array(BOARD_HEIGHT).fill(0);
  const nonGarbages = new Array(BOARD_HEIGHT).fill(0);
  for (let row = 0; row < BOARD_HEIGHT; row++) {
    for (let col = 0; col < BOARD_WIDTH; col++) {
      if (gen.board[row][col] !== 'G') nonGarbages[row] += 1;
      if (gen.board[row][col] !== 'G' && row < height[col]) holes[row] += 1;
    }
  }
  let noOfNonCheeseHoles = 0;
  let isCheeseLevel = true;
  for (let i = 0; i < BOARD_HEIGHT; i++) {
    if (nonGarbages[i] !== 1) isCheeseLevel = false;
    if (!isCheeseLevel) noOfNonCheeseHoles += holes[i];
  }
  const threshold = gen.moreHoles ? 1 : 0;
  return noOfNonCheeseHoles <= threshold;
}

export function tryAPiece(gen: GenState, mode: TSpinMode, opts: GenOptions): boolean {
  if (!tryDrop(gen)) return false;
  let test = !isFloatable(gen);
  const shape = toShape(gen.piece);
  test = test && gen.addedLine.every((row) => shape.some(([, r]) => r === row));
  test = test && isFewNonCheeseHole(gen, mode, opts);
  test = test && getUnstability(gen) === 0;
  test = test && (isExposed(gen) || isSpinable(gen));
  for (const [col, row] of shape) gen.board[row][col] = 'G';
  return test;
}

/**
 * How many copies of each piece a generation attempt may use as filler —
 * 'tsd'/'tst' reserve 1 T for their own finisher clear, 'stsd' reserves 2
 * (both T's are appended to the queue afterward, never drawn as filler).
 */
function pieceLimitFor(mode: TSpinMode, uniquePieces: boolean): Record<PieceType, number> {
  const limit: Record<PieceType, number> = uniquePieces
    ? { I: 1, O: 1, T: 1, J: 1, L: 1, Z: 1, S: 1 }
    : { I: 2, O: 2, T: 2, J: 2, L: 2, Z: 2, S: 2 };
  limit.T -= mode === 'stsd' ? 2 : 1;
  return limit;
}

export function isEvenDistributed(bag: PieceType[], mode: TSpinMode, uniquePieces: boolean): boolean {
  let lastPiece: PieceType | null = null;
  const counter: Record<PieceType, number> = { I: 0, O: 0, T: 0, J: 0, L: 0, Z: 0, S: 0 };
  const limit = pieceLimitFor(mode, uniquePieces);
  for (const piece of bag) {
    counter[piece] += 1;
    if (counter[piece] > limit[piece]) return false;
    if (piece === lastPiece) return false;
    lastPiece = piece;
  }
  if (counter.I + counter.J + counter.L > 4) return false;
  return true;
}

export function tryAllPieces(gen: GenState, mode: TSpinMode, opts: GenOptions): boolean {
  const seenBag: PieceType[] = [];
  const unseenBag: PieceType[] = [];
  for (const piece of PIECE_TYPES) {
    if (gen.pieceAdded.includes(piece)) seenBag.push(piece);
    else unseenBag.push(piece);
  }
  shuffle(seenBag);
  shuffle(unseenBag);
  const bag = unseenBag.concat(seenBag);
  for (const piece of bag) {
    if (!isEvenDistributed([...gen.pieceAdded, piece], mode, opts.uniquePieces)) continue;
    const configs = shuffle([...POSSIBLE_PIECE_CONFIG_TABLE[piece]]);
    for (const [oriIdx, colIdx] of configs) {
      gen.piece.piece = piece;
      gen.piece.x = colIdx;
      gen.piece.y = 18;
      gen.piece.orientation = oriIdx;
      if (tryAPiece(gen, mode, opts)) {
        gen.pieceAdded.push(piece);
        return true;
      }
    }
  }
  return false;
}

export function tryAMove(gen: GenState, mode: TSpinMode, opts: GenOptions): boolean {
  if (gen.moreSkims) addRandomLine(gen);
  else addRandomLineLessSkim(gen);
  if (tryAllPieces(gen, mode, opts)) {
    lockPiece(gen.board, gen.piece);
    gen.boardHistory.push(cloneBoard(gen.board));
    return true;
  }
  return false;
}

export function generateADsMap(gen: GenState, move: number, mode: TSpinMode, opts: GenOptions, unreservedCount: number): boolean {
  for (let trial = 0; trial < 5; trial++) {
    const success = move === 1 ? tryAMove(gen, mode, opts) : tryAMove(gen, mode, opts) && generateADsMap(gen, move - 1, mode, opts, unreservedCount);
    if (success) return true;
    // Anti-stall: every T-spin target is complex enough that this check
    // (ported from the reference engine's skim_ind-or-TSD-mode gate) always
    // applies here.
    const info = `${gen.piece.piece} ${gen.piece.x} ${gen.piece.y} ${gen.piece.orientation}`;
    if (info === gen.lastInfo) trial += 2;
    gen.lastInfo = info;
    const keep = unreservedCount - move;
    gen.boardHistory.length = Math.max(0, keep);
    gen.pieceAdded.length = Math.max(0, keep);
    gen.board = gen.boardHistory.length > 0 ? cloneBoard(gen.boardHistory[gen.boardHistory.length - 1]) : cloneBoard(gen.finishedMap);
  }
  return false;
}

export function isGoodQueue(queue: PieceType[]): boolean {
  let accum: PieceType[] = [];
  let count = 1;
  for (const ele of queue) {
    if (accum.includes(ele)) {
      accum = [];
      count++;
    }
    accum.push(ele);
  }
  return count <= 2;
}

export function getShuffledHoldableQueue(queue: PieceType[]): PieceType[] {
  const size = queue.length;
  if (size >= 2 && size <= 7) {
    const table = shuffle([...REVERSE_HOLD_TABLE[size]]);
    for (const row of table) {
      const result = row.map((i) => queue[i]);
      if (isGoodQueue(result)) return result;
    }
  }
  return [];
}

export function allGrounded(board: Board): boolean {
  for (let col = 0; col < BOARD_WIDTH; col++) {
    let isGrounded = true;
    for (let row = 0; row < BOARD_HEIGHT; row++) {
      if (board[row][col] !== 'N') {
        if (!isGrounded) return false;
      } else {
        isGrounded = false;
      }
    }
  }
  return true;
}

/** Pre-stages the player's hold slot with queue[0] (matching the reference
 * engine's `play()`), and returns the remaining queue to actually play. */
export function splitHoldAndQueue(queue: PieceType[]): { hold: PieceType | null; rest: PieceType[] } {
  if (queue.length === 0) return { hold: null, rest: [] };
  return { hold: queue[0], rest: queue.slice(1) };
}
